'use client'
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import BreadCrumb from '@/components/BreadCrumb';
import { usePathname } from 'next/navigation';
import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { noto_sans, noto_serif } from '@/globalConstants';
import Link from 'next/link';
import BlogCard from '@/components/EngagementContent/BlogCard';
import NudgeBar from '@/components/EngagementContent/NudgeBar';
import BlogContent from '@/components/EngagementContent/BlogContent';
import { Article } from '@/globalTypes';
import { getResponsiveImageUrl } from '@/lib/globalUtils';
import { useAppContext } from '@/contexts/AppContext';

const BlogView = ({ contentSlug }: { contentSlug: string }) => {


    const pathname = usePathname();
    const { isAppView } = useAppContext();
    const [article, setArticle] = useState<Article>({
        thumbnail: "",
        heading: "",
        tags: [],
        publishDate: "",
        readTime: 0,
        banner: "",
        authorName: "",
        relatedBlogs: [],
        mainImage: {},
        tagline: "",
        authorImage: "",
        content: [],
        seo: {}
    });
    const [loader, setLoader] = useState(true);
    useEffect(() => {
        (async () => {
            setLoader(true)
            let response = (await (await fetch(`${process.env.NEXT_PUBLIC_STRAPI_BASE_URL}blogs?filters[slug][$eq]=${contentSlug}`)).json());
            setArticle(response[0]);
            setLoader(false);
        })()
    }, [contentSlug])




    return (
        <div className="w-full min-h-screen  py-8 md:py-12  px-2 ">
            {/* Breadcrumb Placeholder */}
            {!isAppView && <div className="mb-12 md:flex md:justify-center  ">
                <BreadCrumb pathname={pathname} />
            </div>
            }
            <div className={`flex flex-col justify-center items-center w-[100%] md:w-[90%]    2xl:w-[70%]  min-h-screen mx-auto ${isAppView ? 'bg-white py-8 px-6 rounded-2xl lg:bg-[transparent]' : ''}`}>
                <h1 className={`${noto_serif.className} font-serif text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:text-center text-[#1D4749] w-full`}>{loader ? <Skeleton variant="rounded" animation="wave" height={70} width={'80%'} className='mx-auto'></Skeleton> : article.heading}</h1>

                {/* Article Summary */}
                <p className={` text-md lg:text-lg text-gray-700 mb-6 md:text-center font-[500] w-full ${noto_sans.className}`}>
                    {loader ? <Skeleton variant="rounded" animation="wave" height={50} width={'70%'} className='mx-auto'></Skeleton> : article.tagline}
                </p>

                {/* Author Information */}
                <div className="flex items-center mb-6 md:justify-center">
                    <div className="flex-shrink-0">
                        {loader ? <Skeleton variant="circular" animation="wave" height={'50px'} width={'50px'}></Skeleton> : <Image src={process.env.NEXT_PUBLIC_ASSETS_BASE_URL_CONTENT + `${article.authorImage}`} alt="Author" width={50} height={50} className="rounded-full" quality={20} priority />}
                    </div>
                    <div className="ml-4 grid gap-[4px]">
                        <p className={`font-semibold text-[#565151] ${noto_sans.className} font-[500] text-[14px]`} >{loader ? <Skeleton variant="rounded" animation="wave" height={'14px'} width={'4rem'}></Skeleton> : article.authorName}</p>
                        <p className={`font-[500] text-[#8F8B8B] ${noto_sans.className}  text-[12px] flex  items-center gap-[4px]`}>{loader ? <Skeleton variant="rounded" animation="wave" height={'14px'} width={'4rem'}></Skeleton>
                            : <>
                                {`${dayjs(article.publishDate).format("DD-MMM-YYYY").replaceAll('-', " ")} `}
                                •
                                {` ${article.readTime}min`}
                            </>}
                        </p>
                    </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap  mb-6 md:justify-center gap-4">
                    {loader ? [1, 2, 3, 4].map((tag) => {
                        return <Skeleton key={Math.random()} variant="rounded" animation="wave" height={'14px'} width={'4rem'}></Skeleton>
                    }) : article.tags.map((tag) => {

                        return <Link key={Math.random()} href={`/articles/tags/${tag.replaceAll(" ", "-")}`} className='transition ease-in-out delay-150  hover:scale-[120%]  duration-300'>
                            <span className={`px-3 py-1 bg-gray-200 text-sm font-[500] rounded-[0.5rem] text-[#EE9322] ${noto_sans.className}`}>{tag}</span>
                        </Link>
                    })
                    }
                </div>

                {/* Main Image */}
                <div className={`relative w-full flex justify-center ${isAppView ? 'items-center' : ''} h-[480px] max-w-[880px] overflow-hidden m-auto m-12 rounded-lg `}>
                    {loader ? (
                        <Skeleton variant="rounded" animation="wave" width={800} height={450} />
                    ) : (
                        <>
                            <div
                                className="absolute inset-0 bg-center bg-cover filter blur-lg"
                                style={{ backgroundImage: `url(${article.banner ? article.banner.thumbnail.url : ''})` }}
                            />
                            <Image
                                src={getResponsiveImageUrl(article)}
                                alt="Main Article Image"
                                layout="responsive"
                                priority
                                width={1}
                                height={1}
                                className="relative z-10 object-contain rounded-[8px] w-full h-full"
                            />
                        </>
                    )}
                </div>

                {/* Article Content */}
                <div className={` prose lg:prose-xl mb-8 mt-6 ${noto_sans.className} text-[#1D4749] font-[500] grid  w-full  ${loader ? 'gap-[2rem]' : 'gap-[0rem]'}`}>
                    {loader ? [1, 2, 3, 4].map((tag, index) => {
                        return <div className='grid gap-[1rem]' key={Math.random()}>
                            <Skeleton variant="rounded" animation="wave" width={"50%"} height={'2rem'}></Skeleton>
                            <div className='grid gap-[0.8rem]'>
                                <Skeleton variant="rounded" animation="wave" height={'1rem'}></Skeleton>
                                <Skeleton variant="rounded" animation="wave" height={'1rem'}></Skeleton>
                                <Skeleton variant="rounded" animation="wave" height={'1rem'}></Skeleton>
                                <Skeleton variant="rounded" animation="wave" height={'1rem'}></Skeleton>
                                <div className='grid md:grid-cols-2'>
                                    <Skeleton variant="rounded" animation="wave" height={'5rem'}></Skeleton>
                                    <Skeleton variant="rounded" animation="wave" height={'5rem'}></Skeleton>
                                </div>
                                <Skeleton variant="rounded" animation="wave" height={'1rem'}></Skeleton>
                            </div>
                        </div>
                    }) : article.content.map((contentBlock, index) => {
                        return <BlogContent key={index} contentBlock={contentBlock} index={index} />
                    })
                    }
                </div>


                {/* Related Articles */}
                <div className={`${article.relatedBlogs.length ? '' : 'hidden'} `}>
                    {<div className={` m-8 lg:m-12`}>
                        <div className={`__apps__heading flex gap-[8px] justify-center pb-5 lg:pb-8`}>
                            <Image src="/union.svg" alt="union icon" width="30" height="6" />
                            <h2 className={`text-2xl md:text-3xl font-semibold text-center ${noto_serif.className} text-[#1D4749]`}>Related Articles</h2>
                            <Image src="/union.svg" alt="union icon" width="30" height="6" className='rotate-180' />
                        </div>
                        <div className="flex flex-wrap justify-center gap-6">
                            {
                                article.relatedBlogs.map((relatedBlog: any, index: number) => {
                                    return (
                                        <Link key={Math.random()} href={`/articles/${relatedBlog.slug}`} className='block  h-auto'>
                                            <BlogCard thumbnailUrl={relatedBlog.banner.thumbnail.url} tags={relatedBlog.tags} heading={relatedBlog.heading} summary={relatedBlog.tagline} publishDate={relatedBlog.publishDate} readTime={relatedBlog.readTime} orientation={'grid'} slug={article.relatedBlogs[index]} />
                                        </Link>
                                    )
                                })
                            }
                        </div>
                    </div>}
                    {/* Call to Action */}
                    {!isAppView && <div className="mt-12 text-center">
                        <a href='/articles' className="px-8 py-3 bg-orange-600 text-white rounded-full font-semibold text-lg hover:bg-orange-500">View All Articles</a>
                    </div>}
                    {isAppView && <div className="mt-12 text-center">
                        <a href='/articles?source=webview' className="px-8 py-3 bg-orange-600 text-white rounded-full font-semibold text-lg hover:bg-orange-500">View All Articles</a>
                    </div>}
                </div>
                {/*App Download Nudgge*/}
                {!isAppView && <NudgeBar />}
            </div>
        </div >
    );
};

export default BlogView;
